import { IAddress, IAvatar } from 'interfaces';
import { EOrderType } from 'services/pos';

export function uniqueBy<T, K extends keyof T>(arr: Array<T>, prop: K): Array<T> {
  return arr.reduce((a, d) => {
    if (!a.find((val) => val[prop] === d[prop])) {
      a.push(d);
    }
    return a;
  }, [] as Array<T>);
}

export function capitalize(word: string) {
  return (word && word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) || '';
}

export function findDuplicate<T, K extends keyof T>(array: Array<T>, key: K) {
  const values = array.map((item) => item[key]);
  return values.filter((item, index) => values.indexOf(item) !== index);
}

export function generateUniqueIdFromString(values: string, uniqueIdLength = 6) {
  const firstCharOfValues = values.match(/\b(\w)/g) || [];
  const joinedString = firstCharOfValues.join('');
  const lengthOfUniqueId = joinedString.length;

  if (lengthOfUniqueId === uniqueIdLength) {
    return joinedString;
  }

  const remainingChars = uniqueIdLength - lengthOfUniqueId;
  if (lengthOfUniqueId > uniqueIdLength) {
    return joinedString.slice(0, uniqueIdLength);
  }

  const newString = Math.random()
    .toString()
    .slice(2, remainingChars + 2);
  const generateUniqueId = joinedString + newString;
  return generateUniqueId;
}

/**
 * Get date range of last x amount of months
 * @param month
 * @returns {[Date, Date]}
 */
export const getDateRangeOfLastXMonths = (
  month: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
): [Date, Date] => {
  const currentDate = new Date();

  const startDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - month,
    currentDate.getDate(),
    0,
    0,
    0,
  );

  const endDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
    23,
    59,
    59,
  );

  return [startDate, endDate];
};

/**
 * Get date range of the last X days
 * @param days - Number of days
 * @returns {[Date, Date]}
 */
export const getDateRangeOfLastXDays = (days: number): [Date, Date] => {
  const currentDate = new Date();

  // If days is 0, return today's range from 12:00 AM to 11:59 PM
  const startDate = new Date(currentDate);
  startDate.setDate(currentDate.getDate() - days);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setHours(23, 59, 59, 999);

  return [startDate, endDate];
};

/**
 * Formats a given date to "YYYY-MM-DD HH:mm" in the local timezone.
 *
 * @param {Date} date - The date to format.
 * @param {number} hours - The hours to set (0-23).
 * @param {number} minutes - The minutes to set (0-59).
 * @returns {string} The formatted date string in "YYYY-MM-DD HH:mm".
 */
export const formatToLocalDateTime = (date: Date, hours: number, minutes: number): string => {
  const localDate = new Date(date);
  localDate.setHours(hours, minutes, 0, 0); // Set specific time (HH:mm)

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Ensure 2-digit format
  const day = String(localDate.getDate()).padStart(2, '0');
  const hour = String(localDate.getHours()).padStart(2, '0');
  const minute = String(localDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minute}`;
};

export const getAddressAsString = (address: Partial<IAddress>) => {
  const { street, area, city, country, zip } = address;

  const addressStrings = [];
  if (street) addressStrings.push(street);
  if (area) addressStrings.push(area);
  if (city && zip) {
    addressStrings.push(`${city}-${zip}`);
  } else if (city) {
    addressStrings.push(city);
  } else if (zip) {
    addressStrings.push(`-${zip}`);
  }
  if (country) addressStrings.push(country);

  return addressStrings.join(', ');
};

export function getRandomString(
  length: number,
  chars: 'a' | 'A' | '#' | 'aA' | 'a#' | 'A#' | 'aA#',
) {
  let mask = '';
  if (chars.includes('a')) mask += 'abcdefghijklmnopqrstuvwxyz';
  if (chars.includes('A')) mask += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (chars.includes('#')) mask += '0123456789';

  let result = '';
  for (let i = length; i > 0; --i) {
    result += mask[Math.floor(Math.random() * mask.length)];
  }
  return result;
}

export function camelCaseToWords(s: string) {
  const result = s.replace(/([A-Z])/g, ' $1').trim();
  return result.charAt(0).toUpperCase() + result.slice(1);
}

// merge product variant
export const mergedVariantLot = (
  lots: {
    id: string;
    unitPrice: number;
    quantity: number;
    productVariant: {
      id: string;
      name: string;
      sku: string;
      photo?: IAvatar;
    };
  }[],
) => {
  return lots.reduce(
    (result, lot) => {
      const { productVariant, ...rest } = lot;
      const { id, name, sku, photo } = productVariant;
      const existedVariant = result.find((item) => item.id === id);
      if (existedVariant) {
        existedVariant.quantity += rest.quantity;
      } else {
        result.push({
          id,
          name,
          sku,
          photo,
          quantity: rest.quantity,
          unitPrice: rest.unitPrice,
        });
      }
      return result;
    },
    [] as {
      id: string;
      name: string;
      sku: string;
      photo?: IAvatar;
      quantity: number;
      unitPrice: number;
    }[],
  );
};

// Lives in utilities/currency so modules without the API client (and their tests) can use it.
export { formatCurrency } from './currency';

export const SKU_REGEX = /^[^\s'"`^!=+*;:~?<>]+$/;
export const NAME_REGEX = /^[^`^!=*;:~?<>]+$/;

export function getInvalidCharacterErrorMessage(fieldName: 'name' | 'sku'): string {
  // const invalidChars = ['`', '^', '!', '=', '+', '*', ';', ':', '~', '?', '<', '>'];
  if (fieldName === 'name') {
    return `The ${fieldName} contains invalid characters: ${[
      '`',
      '^',
      '!',
      '=',
      '*',
      ';',
      ':',
      '~',
      '?',
      '<',
      '>',
    ].join(' ')}`;
  }

  if (fieldName === 'sku') {
    return `The ${fieldName} contains invalid characters: ${[
      '`',
      '^',
      '!',
      '=',
      '+',
      '*',
      ';',
      ':',
      '~',
      '?',
      '<',
      '>',
    ].join(' ')}`;
  }

  return '';
}

type Action = 'create' | 'list' | 'show' | 'edit' | 'delete'; // Add other actions as needed

interface PermissionData {
  role: string;
  resource: string;
  actions: string;
}

interface PermissionsObject {
  [role: string]: {
    [resource: string]: Action[];
  };
}

export const transformToPermissionsObject = (dataArray: PermissionData[]): PermissionsObject => {
  const permissionsObject: PermissionsObject = {};

  dataArray.forEach(({ role, resource, actions }) => {
    // Remove parentheses from actions and split by `|`
    const actionList: Action[] = actions.replace(/[()]/g, '').split('|') as Action[];

    // Initialize the role if not already present
    if (!permissionsObject[role]) {
      permissionsObject[role] = {};
    }

    // If the resource is already present, merge actions, else initialize it
    if (permissionsObject[role][resource]) {
      permissionsObject[role][resource] = [
        ...new Set([...permissionsObject[role][resource], ...actionList]),
      ];
    } else {
      permissionsObject[role][resource] = actionList;
    }
  });

  // Convert the nested structure to a flattened version
  const flattenedPermissionsObject: PermissionsObject = {};
  Object.keys(permissionsObject).forEach((role) => {
    Object.keys(permissionsObject[role]).forEach((resource) => {
      if (resource.includes('/*')) {
        const baseResource = resource.split('/*')[0];
        if (!flattenedPermissionsObject[role]) {
          flattenedPermissionsObject[role] = {};
        }
        flattenedPermissionsObject[role][baseResource] = [
          ...(flattenedPermissionsObject[role][baseResource] || []),
          ...permissionsObject[role][resource],
        ];
      } else {
        if (!flattenedPermissionsObject[role]) {
          flattenedPermissionsObject[role] = {};
        }
        flattenedPermissionsObject[role][resource] = [
          ...(flattenedPermissionsObject[role][resource] || []),
          ...permissionsObject[role][resource],
        ];
      }
    });
  });

  return flattenedPermissionsObject;
};

export const scrollToFieldWithOffset = (fieldName: string, offset: number) => {
  const fieldElement = document.querySelector(`[id="${fieldName}"]`);

  if (fieldElement) {
    const rect = fieldElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Scroll to the field with added offset
    window.scrollTo({
      top: rect.top + scrollTop - offset,
      behavior: 'smooth',
    });
  }
};

export const UNITS_OF_MEASURE = [
  { value: 'gram', label: 'Gram (g)' },
  { value: 'kilogram', label: 'Kilogram (kg)' },
  { value: 'milligram', label: 'Milligram (mg)' },
  { value: 'pound', label: 'Pound (lb)' },
  { value: 'ounce', label: 'Ounce (oz)' },
  { value: 'liter', label: 'Liter (L)' },
  { value: 'gallon', label: 'Gallon (gal)' },
  { value: 'pcs', label: 'Unit (pcs)' },
  { value: 'pack', label: 'Pack (pk)' },
  { value: 'dozen', label: 'Dozen (dz)' },
  { value: 'set', label: 'Set (st)' },
];

export const ProductTemplateTypeMap = {
  text: 'Text' as const,
  float: 'Float' as const,
  boolean: 'Boolean' as const,
  dropdown: 'Dropdown' as const,
};

export const OrderTypeMapping = {
  [EOrderType.DineIn]: 'Dine-In',
  [EOrderType.Takeaway]: 'Parcel',
};

interface Recipe {
  usedProductCost?: number;
}

interface Wastage {
  cost?: number | string;
}

interface Expense {
  amount?: number | string;
}

export const calculateTotalManufacturingCost = (
  recipeData: Recipe[] = [],
  wastageData: Wastage[] = [],
  expenseData: Expense[] = [],
): number => {
  const totalMaterialCost = recipeData.reduce(
    (sum, recipe) => sum + (recipe.usedProductCost || 0),
    0,
  );

  const totalWastageCost = wastageData.reduce(
    (sum, wastage) => sum + (Number(wastage.cost) || 0),
    0,
  );

  const totalExpense = expenseData.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);

  return totalMaterialCost + totalWastageCost + totalExpense;
};

export function downloadBase64Pdf(base64: string, filename = 'file.pdf') {
  const link = document.createElement('a');
  link.href = `data:application/pdf;base64,${base64}`;
  link.download = filename;
  link.click();
}

// export function previewPdfFromBase64(base64: string) {
//   const byteCharacters = atob(base64);
//   const byteArray = new Uint8Array([...byteCharacters].map((c) => c.charCodeAt(0)));
//   const blob = new Blob([byteArray], { type: 'application/pdf' });
//   const blobUrl = URL.createObjectURL(blob);

//   window.open(blobUrl);
// }

export function previewPdfFromBase64(base64: string) {
  // Decode base64 to Uint8Array (efficient method for large PDFs)
  const binary = atob(base64);
  const len = binary.length;
  const byteArray = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    byteArray[i] = binary.charCodeAt(i);
  }

  // Create a Blob URL
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);

  // Remove old modal if exists
  const oldModal = document.getElementById('pdf-modal');
  if (oldModal) oldModal.remove();

  // Create modal HTML
  const modal = document.createElement('div');
  modal.id = 'pdf-modal';
  modal.innerHTML = `
    <div class="pdf-backdrop"></div>
    <div class="pdf-dialog">
      <button class="pdf-close" aria-label="Close PDF">×</button>
      <iframe src="${blobUrl}" frameborder="0" title="PDF Preview"></iframe>
    </div>
  `;
  document.body.appendChild(modal);
  document.body.style.overflow = 'hidden'; // block page scroll

  // Define close modal function
  const closeModal = () => {
    modal.classList.remove('show');
    setTimeout(() => {
      modal.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', escListener);
      URL.revokeObjectURL(blobUrl);
    }, 300);
  };

  // Escape key support
  const escListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  };
  document.addEventListener('keydown', escListener);

  // Bind close events
  modal.querySelector('.pdf-close')?.addEventListener('click', closeModal);
  modal.querySelector('.pdf-backdrop')?.addEventListener('click', closeModal);

  // Animate modal in
  setTimeout(() => modal.classList.add('show'), 10);

  // Trigger print when iframe loads
  const iframe = modal.querySelector('iframe') as HTMLIFrameElement;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        console.error('Print failed:', err);
      }
    }, 500); // Delay to ensure rendering is complete
  };

  iframe.onerror = () => {
    console.error('Failed to load the PDF preview.');
    closeModal();
  };
}

export const IS_DISCOUNT_IN_PERCENTAGE = 'IS_DISCOUNT_IN_PERCENTAGE';


export function excelSafeImport(value: string): string {
  if (!value) return value;

  let v = String(value).trim();

  if (/^=".*"$/.test(v)) {
    v = v.slice(2, -1);
  }

  if (v.startsWith("'")) {
    v = v.slice(1);
  }

  return v;
}

export const UNIQUE_ID_SEPARATOR = '|';

const UNIQUE_ID_SEGMENT = /^[A-Za-z0-9-]+$/;

/**
 * Parses the composite "Unique ID" cell of a product/variant update CSV.
 *
 * Current format: `<productId>|<variantId>`
 * Legacy format:  `<productId><productName><variantId>` — the name segment was decorative
 *                 and is ignored. It is still accepted so CSVs exported before the format
 *                 change keep importing.
 *
 * Returns `null` when the value matches neither shape, so callers can fail loudly instead
 * of silently falling back to grouping by product name.
 */
export function parseProductUniqueId(
  value: string,
): { productId: string; variantId: string } | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;

  const build = (productId: string, variantId: string) =>
    UNIQUE_ID_SEGMENT.test(productId) && UNIQUE_ID_SEGMENT.test(variantId)
      ? { productId, variantId }
      : null;

  const separatorIndex = raw.indexOf(UNIQUE_ID_SEPARATOR);
  if (separatorIndex !== -1) {
    return build(raw.slice(0, separatorIndex), raw.slice(separatorIndex + 1));
  }

  // Legacy `id<name>id`. Use the last '>' so a product name containing '>' still parses.
  const nameStart = raw.indexOf('<');
  const nameEnd = raw.lastIndexOf('>');
  if (nameStart === -1 || nameEnd === -1 || nameEnd < nameStart) return null;

  return build(raw.slice(0, nameStart), raw.slice(nameEnd + 1));
}

export { csvEscape, excelSafeExport } from './csv';

// export function csvEscape(value: string): string {
//   if (value === null || value === undefined) return '';

//   let str = String(value);

//   // Escape double quotes by doubling them
//   str = str.replace(/"/g, '""');

//   // Wrap in quotes if it contains special CSV chars
//   if (/[",\n\r]/.test(str)) {
//     str = `"${str}"`;
//   }

//   return str;
// }

export const removeEmptyObject = (obj: Record<string, any>, key: string) => {
  const value = obj[key];
  if (Object.prototype.toString.call(value) !== '[object Object]') {
    return;
  }

  const objectValue = value as Record<string, unknown>;
  if (
    Object.keys(objectValue).length === 0 ||
    Object.values(objectValue).every(
      (nestedValue) =>
        nestedValue === null ||
        nestedValue === undefined ||
        (typeof nestedValue === 'string' && nestedValue.trim() === ''),
    )
  ) {
    delete obj[key];
  }
};
